import React, { FunctionComponent, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { auroraBaseColors } from '@lyrahealth-inc/shared-app-logic'

import { Tooltip } from '../../atoms'
import { BaseButton, ButtonSize, ButtonType } from '../../atoms/baseButton/BaseButton'
import { HeadphoneHeartIcon } from '../../atoms/illustrations/HeadphoneHeartIcon'
import TertiaryIconButton from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils'
import { tID } from '../../utils/utils'

export interface CareNavigatorHeaderEntryPointButtonProps {
  isInternational?: boolean
  onPress: () => void
  isActive?: boolean
  lightBgColor?: boolean
  testID?: string
  suppressAccessibilitySelected?: boolean
  useSimplifiedCNIconWithTooltip?: boolean
  onIconHover?: ({ hoverText }: { hoverText: string }) => void
}

const StyledBaseButton = styled(BaseButton)<{ theme: ThemeType }>(({ theme }) => ({
  justifyContent: 'center',
  paddingTop: theme.spacing['8px'],
  paddingBottom: theme.spacing['8px'],
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  borderWidth: 1.5,
  height: '40px', // intentionally specific height given by design
  borderColor: theme.colors.borderDefault,
}))

const AuroraIconCircle = styled.View({
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: auroraBaseColors.white_opacity_10,
  borderWidth: 1,
  borderColor: auroraBaseColors.white_opacity_20,
  justifyContent: 'center',
  alignItems: 'center',
})

export const CareNavigatorHeaderEntryPointButton: FunctionComponent<CareNavigatorHeaderEntryPointButtonProps> = ({
  isInternational = false,
  onPress,
  onIconHover = noop,
  useSimplifiedCNIconWithTooltip = false,
  isActive = false,
  lightBgColor = true,
  testID = tID('CareNavigatorHeaderEntryPoint-button'),
  ...restProps
}) => {
  const { breakpoints, colors, isAuroraTheme } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const tooltipRef = useRef<{ show: () => void; hide: () => void }>(null)

  const buttonColorText = colors.tertiaryButtonTextActive
  const auroraIconColor = colors.textInverse
  const buttonColorIcon = isAuroraTheme
    ? auroraIconColor
    : lightBgColor
    ? colors.iconLightBackground
    : colors.iconDarkBackground
  const buttonText = isInternational ? (
    <FormattedMessage
      defaultMessage='Contact us'
      description='Label of button that takes the user to the page to get in touch with a Care Navigator'
    />
  ) : (
    <FormattedMessage
      defaultMessage='Get in touch'
      description='Label of button that takes the user to the page to get in touch with a Care Navigator'
    />
  )

  /* CN icon wrapped in tooltip on web. TertiaryIconButton is not a child of Tooltip in this case because of accessibility
     violations from nesting interactive elements, hence the absolute positioning. On mobile screens the tooltip is not rendered */
  const iconButtonWithWebTooltip = (
    <>
      {IS_WEB && breakpoints.isMinWidthTablet && (
        <Tooltip
          ref={tooltipRef}
          content={buttonText}
          hoverEnabled={true}
          style={IS_WEB ? { outline: 'none', position: 'absolute', top: '100%', right: '50%', marginTop: 40 } : {}}
        >
          <></>
        </Tooltip>
      )}
      <TertiaryIconButton
        testID={testID}
        onPress={onPress}
        leftIcon={
          isAuroraTheme ? (
            <AuroraIconCircle>
              <HeadphoneHeartIcon fill={buttonColorIcon} heartFill={auroraIconColor} />
            </AuroraIconCircle>
          ) : (
            <HeadphoneHeartIcon fill={buttonColorIcon} />
          )
        }
        accessibilityLabel={formatMessage({
          defaultMessage: 'Care Navigator',
          description: 'Care navigator entry point',
        })}
        size={ButtonSize.SMALL}
        onHoverIn={() => {
          tooltipRef.current?.show()
          onIconHover({ hoverText: isInternational ? 'Contact us' : 'Get in touch' }) // Track hover in event for tooltip
        }}
        onHoverOut={() => tooltipRef.current?.hide()}
        style={isAuroraTheme ? { padding: 0 } : undefined}
        {...restProps}
      />
    </>
  )

  return !breakpoints.isMinWidthTablet || useSimplifiedCNIconWithTooltip ? (
    iconButtonWithWebTooltip
  ) : (
    <StyledBaseButton
      selected={isActive}
      testID={testID}
      onPress={onPress}
      text={buttonText}
      leftIcon={<HeadphoneHeartIcon fill={buttonColorIcon} />}
      customTextColor={buttonColorText}
      buttonType={ButtonType.TERTIARY_HEADER}
      {...restProps}
    />
  )
}
